import type {
  AccountActivateResponse,
  B2BRegistrationResponse,
  RegistrationResponse,
  CSRFTokenResponse,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  RefreshTokenResponse,
  ResetPasswordResponse,
} from '@/interfaces/api/app';
import type {
  ActivateAccountMethod,
  B2BRegistrationMethod,
  ClearTokensMethod,
  RegistrationMethod,
  CredentialsLoginProps,
  ForgotPasswordMethod,
  //GetProfileMethod,
  GetTokensMethod,
  GetUserMethod,
  IAuthService,
  LoginMethod,
  LogoutMethod,
  RefreshTokensMethod,
  ResetPasswordMethod,
  SetTokensMethod,
} from '../Auth.types';
import { api, fetcher } from '@/shared/utils/api';
import { authStorage } from '../config/auth-storage';
import type {
  User,
  EditAccountRequest,
  EditAccountResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '@/interfaces/api/account';
import type { UpdateSupplierProfileRequest } from '@/interfaces/api/app';

class AuthService implements IAuthService {
  private static instance: AuthService;

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public login: LoginMethod = async props => {
    const { email, password } = props as CredentialsLoginProps;
    const requestObject: LoginRequest = {
      email,
      password,
    };

    return await fetcher<LoginResponse>({
      url: '/v1/auth/login',
      options: {
        method: 'POST',
        body: JSON.stringify(requestObject),
      },
    });
  };

  public logout: LogoutMethod = async props => {
    const { accessToken, refreshToken } = props;

    // Send 2 requests to invalidate both access and refresh tokens (if provided)
    if (accessToken) {
      await fetcher<LogoutResponse>({
        url: '/v1/auth/logout',
        options: {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      });
    }

    if (refreshToken) {
      await fetcher<LogoutResponse>({
        url: '/v1/auth/logout',
        options: {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        },
      });
    }

    return { logout: true };
  };

  public register: RegistrationMethod = async props => {
    return await fetcher<RegistrationResponse>({
      url: '/v1/auth/register',
      options: {
        method: 'POST',
        body: JSON.stringify({
          ...props,
        }),
      },
    });
  };

  public b2bRegister: B2BRegistrationMethod = async props => {
    return await fetcher<B2BRegistrationResponse>({
      url: '/v1/auth/register/b2b',
      options: {
        method: 'POST',
        body: JSON.stringify({ ...props }),
      },
    });
  };

  public activateAccount: ActivateAccountMethod = async props => {
    const { token } = props;

    return await fetcher<AccountActivateResponse>({
      url: '/v1/auth/activate',
      options: {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
    });
  };

  public forgotPassword: ForgotPasswordMethod = async props => {
    const { email } = props;

    return await fetcher<ForgotPasswordResponse>({
      url: '/v1/auth/forgot-password',
      options: {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    });
  };

  public resetPassword: ResetPasswordMethod = async props => {
    const { password, confirm_password, resetPasswordToken } = props;

    return await fetcher<ResetPasswordResponse>({
      url: '/v1/auth/reset-password',
      options: {
        method: 'POST',
        body: JSON.stringify({ password, confirm_password, token: resetPasswordToken }),
      },
    });
  };

  public getUser: GetUserMethod = async props => {
    const { accessToken } = props;

    return await fetcher<User>({
      url: '/v1/account/',
      requiresAuth: true,
      options: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    });
  };

  /* public getProfile: GetProfileMethod = async props => {
    const { accessToken } = props;

    return await fetcher<User>({
      url: '/v1/account/profile',
      requiresAuth: true,
      options: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    });
  }; */

  public setTokens: SetTokensMethod = props => {
    const { accessToken, refreshToken, csrfToken } = props;

    if (accessToken) {
      authStorage.setItem('access_token', accessToken);
    }

    if (refreshToken) {
      authStorage.setItem('refresh_token', refreshToken);
    }

    if (csrfToken) {
      authStorage.setItem('csrf_token', csrfToken);
    }
  };

  public getTokens: GetTokensMethod = () => {
    const accessToken = authStorage.getItem('access_token') ?? undefined;
    const refreshToken = authStorage.getItem('refresh_token') ?? undefined;
    const csrfToken = authStorage.getItem('csrf_token') ?? undefined;

    return { accessToken, refreshToken, csrfToken };
  };

  public clearTokens: ClearTokensMethod = () => {
    authStorage.removeItem('access_token');
    authStorage.removeItem('refresh_token');
    authStorage.removeItem('csrf_token');

    // Notify other tabs
    window.dispatchEvent(new Event('storage'));

    // Manually notify current tab as well
    const storageEvent = new StorageEvent('storage', {
      key: 'forced_wipe',
      oldValue: null,
      newValue: null,
      storageArea: authStorage,
      url: window.location.href,
    });

    window.dispatchEvent(storageEvent);
  };

  public refreshTokens: RefreshTokensMethod = async props => {
    const { refreshToken } = props;

    if (!refreshToken) {
      throw new Error('refreshTokens method missing following props; { refreshToken }');
    }

    return await fetcher<RefreshTokenResponse>({
      url: '/v1/auth/token-refresh',
      options: {
        method: 'POST',
        headers: { Authorization: `Bearer ${refreshToken}` },
      },
    });
  };

  public updateB2BProfile = async (payload: UpdateSupplierProfileRequest) => {
    return await fetcher({
      url: '/v1/b2b/profile',
      requiresAuth: true,
      options: {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    });
  };

  public updateAccount = async (data: EditAccountRequest): Promise<EditAccountResponse> => {
    return await fetcher<EditAccountResponse>({
      url: '/v1/account/',
      requiresAuth: true,
      options: {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    });
  };

  public changePassword = async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    return await fetcher<ChangePasswordResponse>({
      url: '/v1/account/change-password',
      requiresAuth: true,
      options: {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    });
  };

  public uploadProfileImage = async (props: { file: File }) => {
    const { accessToken, csrfToken } = this.getTokens();
    const { file } = props;

    const formData = new FormData();
    formData.append('file', file);

    return await api({
      endpoint: '/v1/account/avatar',
      options: {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-CSRF-Token': csrfToken ?? '',
        },
        body: formData,
      },
    });
  };

  /**
   * Fetch and set CSRF token in `authStorage`
   */
  public CSRFToken = async () => {
    const { csrf_token } = await fetcher<CSRFTokenResponse>({
      url: '/v1/public/csrf',
      requiresAuth: false,
    });

    if (csrf_token) {
      try {
        authStorage.setItem('csrf_token', csrf_token);
      } catch (error) {
        console.error(error);
      }
    }

    return { csrf_token };
  };
}

export default AuthService.getInstance();
